import { useEffect, useState } from 'react';

import { useSuspenseXinglet } from '../hooks/use-suspense-xinglet';
import type { Route } from '../route-context';
import type { XingletLoaderProps } from '../xinglet';

import type { Runtime } from './runtime';
import { useRuntime } from './use-runtime';

const EmptyComponent: React.FC<{ name: string; route?: Route }> = ({
  name,
  route,
}) => {
  if (route) {
    throw new Error(
      `Xinglet "${name}" didn't provide a component for route ${route.route}.`
    );
  } else {
    throw new Error(`Xinglet "${name}" didn't export any component.`);
  }
};

const RawXingletLoader: React.FC<{ name: string; route?: Route }> = ({
  name,
  route,
  ...rest
}) => {
  let Component: React.ComponentType | undefined;
  const { Component: MainComponent, module } = useSuspenseXinglet(name);
  const { matchRoute, routes } = module;

  if (route && (matchRoute || routes)) {
    Component = matchRoute?.(route) ?? routes?.[route.route] ?? routes?.['*'];
  } else {
    Component = MainComponent;
  }

  if (!Component) {
    return <EmptyComponent name={name} route={route} />;
  }

  return <Component {...rest} />;
};

const DevWrapper: React.FC<
  React.PropsWithChildren<{
    name: string;
    runtime: Runtime;
  }>
> = ({ children, name: initialName, runtime }) => {
  const [{ name }, setState] = useState<{ name: string }>({
    name: initialName,
  });

  useEffect(() => {
    return runtime.events.on('xinglet-reloaded', (otherName) => {
      if (otherName === name) setState({ name });
    });
  }, [runtime, name]);

  return children;
};

const SSRWrapper: React.FC<
  React.PropsWithChildren<{
    fallback?: React.ReactNode;
    name: string;
    runtime: Runtime;
  }>
> = ({ children, fallback, name, runtime }) => {
  const { manifestMap } = runtime;
  const ssr = manifestMap[name]?.metadata?.ssr === true;
  const [enabled, setEnabled] = useState(ssr);

  useEffect(() => setEnabled(true), []);

  return <>{enabled ? children : fallback}</>;
};

type InternalXingletLoader = XingletLoaderProps & { route?: Route };

export const InternalXingletLoader: React.FC<InternalXingletLoader> = ({
  fallback,
  name,
  route,
  ...rest
}) => {
  const runtime = useRuntime();
  let element = <RawXingletLoader name={name} route={route} {...rest} />;

  if (LOCAL_DEVELOPMENT) {
    element = (
      <DevWrapper name={name} runtime={runtime}>
        {element}
      </DevWrapper>
    );
  }

  return (
    <SSRWrapper fallback={fallback} name={name} runtime={runtime}>
      {element}
    </SSRWrapper>
  );
};
